import {Typography} from '@mui/material';
import classNames, {Argument} from 'classnames';
import * as React from 'react';
import {useEffect, useRef} from 'react';

import {DlMuiPopper} from './DlMuiPopper';
import styles from './SubmitButton.less';
import {useMuiAnchor} from '../hooks';

interface OwnProps {
  alignSelf?: string;
  className?: Argument;
  dataName?: string;
  disabled?: boolean;
  focusOnMount?: boolean;
  isPrimary?: boolean;
  isDanger?: boolean;
  noColor?: boolean;
  onClick: (event: React.UIEvent<HTMLButtonElement>) => void;
  setButtonRef?: (button: HTMLButtonElement) => void;
  title?: string;
}

type Props = OwnProps;

export const SubmitButton: React.FC<Props> = (props) => {
  const {
    alignSelf = 'flex-end',
    children,
    className = '',
    dataName,
    disabled = false,
    focusOnMount = false,
    isPrimary = true,
    isDanger = false,
    noColor = false,
    setButtonRef,
    title = '',
  } = props;

  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (focusOnMount && buttonRef.current) {
      buttonRef.current.focus();
    }
  }, [focusOnMount]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const {onClick} = props;
    event.stopPropagation();
    onClick(event);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    const {onClick} = props;
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
      onClick(event);
    }
    return true;
  };

  const hasTooltip = !!title;

  const {mouseEventEnter, mouseEventLeave, open, anchorEl} =
    useMuiAnchor(hasTooltip);

  return (
    <>
      <button
        onMouseEnter={mouseEventEnter}
        onMouseLeave={mouseEventLeave}
        type={isPrimary ? 'submit' : 'button'}
        className={classNames(className, styles.dlSubmitButton, 'btn', {
          'btn-primary': isPrimary && !noColor,
          [styles.dlSubmitButtonPrimary]: isPrimary && !noColor,
          [styles.dlSubmitButtonDanger]: !isPrimary && isDanger && !noColor,
          [styles.dlSubmitButtonNormal]: !isPrimary && !isDanger && !noColor,
          [styles.dlAlignFlexEnd]: alignSelf === 'flex-end',
          [styles.dlAlignFlexStart]: alignSelf === 'flex-start',
          [styles.dlAlignFlexCenter]: alignSelf === 'center',
        })}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        ref={(button: HTMLButtonElement) => {
          buttonRef.current = button;
          if (setButtonRef) {
            setButtonRef(button);
          }
        }}
        tabIndex={0}
        disabled={disabled}
        data-name={dataName}
      >
        <span className={styles.dlSubmitButtonContent}>{children}</span>
      </button>
      <DlMuiPopper hasTooltip={hasTooltip} anchorEl={anchorEl} open={open}>
        <Typography variant={'popper'}>{title}</Typography>
      </DlMuiPopper>
    </>
  );
};

SubmitButton.displayName = 'SubmitButton';
